/**
 * AccessRequestDialog — User asks for temporary access to a locked button.
 *
 * Triggered by the padlock badge on any locked button. Lets the user
 * pick 1h / 2h / 1d, optionally add a reason, and posts to
 * /api/access-requests. The admin receives an in-app notification +
 * sees the request on the dashboard Access Requests tab.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Lock, Clock, Send, X, Loader2 } from 'lucide-react';
import { deptsForButton, deptLabel } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccessRequestDialog({ open, onClose, button, buttonLabel }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [hours, setHours] = useState(1);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const required = deptsForButton(button);

  const submit = async () => {
    if (!token) {
      toast.error('Sign in first.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/access-requests`,
        { button, duration_hours: hours, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Request sent. An admin will review it shortly.');
      setReason('');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="access-request-dialog"
        className="max-w-md w-[95vw] p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">Request access</DialogTitle>
        <DialogDescription className="sr-only">
          Ask an admin for temporary access to {buttonLabel || button}.
        </DialogDescription>

        <div
          className="px-5 py-4 flex items-center gap-3"
          style={{ background: 'linear-gradient(135deg, #B45309 0%, #D97706 100%)', fontFamily: 'Manrope' }}
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/85">Locked feature</p>
            <h2 className="text-base font-bold text-white truncate">{buttonLabel || button}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white hover:bg-white/15" data-testid="access-request-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4" style={{ background: '#FAF8F2', fontFamily: 'Manrope' }}>
          {required.length > 0 && (
            <p className="text-xs" style={{ color: '#5C6B6B' }}>
              This button is normally accessible to: <strong style={{ color: '#1A2E2E' }}>{required.map(deptLabel).join(', ')}</strong>. You can ask an admin for temporary access.
            </p>
          )}

          {/* Duration picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>
              How long do you need access?
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { v: 1,  l: '1 hour'  },
                { v: 2,  l: '2 hours' },
                { v: 24, l: '1 day'   },
              ].map((opt) => {
                const active = hours === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setHours(opt.v)}
                    data-testid={`access-request-duration-${opt.v}h`}
                    className="px-3 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-1 transition"
                    style={{
                      background: active ? 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' : 'white',
                      color: active ? 'white' : '#1A2E2E',
                      border: active ? '2px solid #003DA5' : '1px solid #D6D0C0',
                    }}
                  >
                    <Clock className="w-3 h-3" /> {opt.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>
              Reason (optional)
            </label>
            <textarea
              data-testid="access-request-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Briefly explain why you need access. Helps admins decide faster."
              className="mt-2 w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={{ borderColor: '#D6D0C0', color: '#1A2E2E', background: 'white' }}
            />
          </div>

          <button
            type="button"
            data-testid="access-request-submit"
            disabled={busy}
            onClick={submit}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-white disabled:opacity-60 transition hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #15803D 0%, #16A34A 100%)', boxShadow: '0 4px 16px rgba(21,128,61,0.35)' }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send request to admin
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
